<template>
  <div class="mb-4">
    <div class="d-flex align-center justify-space-between gap10">

      <div class="d-flex align-center flex-wrap gap15">
        <result-table-mode-selector
          v-if="hasLabeledKeywords"
          :value="showGraphChart"
          @input="$emit('update:showGraphChart', $event)"
        />

        <div class="select-all-group d-flex align-center" v-if="!showGraphChart">
          <v-checkbox
            class="wg-result-select-all-checkbox ma-0"
            color="success"
            off-icon="none"
            on-icon="check"
            :ripple="false"
            hide-details
            style="margin-left: -10px !important; padding-top: 0 !important;"
            :disabled="selectAllDisabled"
            :input-value="selectAllChecked"
            :indeterminate="selectAllIndeterminate"
            @change="$emit('toggle-select-all')"
          >
            <template v-slot:label>
              <div class="d-flex align-center" style="text-wrap: nowrap; color: var(--v-blackColor-base); font-weight: 700;">
                <span>Select all</span>
                <span
                  v-if="selectedCount > 0"
                  class="wg-result-selected-count"
                >Selected ({{ selectedCount }})</span>
              </div>
            </template>
          </v-checkbox>
        </div>

        <a v-if="hiddenRows.length" href="#" @click.prevent="$emit('update:showHiddenRowsModal', true)" class="font-weight-bold">Hidden rows ({{ hiddenRows.length }})</a>
      </div>

      <!--
      <div
        v-if="reportType === 'KeywordResearch'"
        style="flex: 1;"
      >
        <div style="color: var(--v-mainColor3-base); font-size: 0.9rem;"><template v-if="report.name"><span>{{ report.name }}</span> <span style="color: var(--v-grayColor-base);">•</span></template> <span>Report ID: #{{ report.id }}</span></div>
      </div>
      -->
 
      <div
        v-if="reportType === 'SiloResearchTopic' && siloResearchType === 'one-click-silo'"
        style="flex: 1;"
      >
        <div class="d-flex gap10 align-center" style="color: var(--v-mainColor3-base); font-size: 0.9rem;">
          <span v-if="parentReport.name" style="font-size: 0.9rem;">{{ parentReport.name }}</span>
          <span v-else style="font-size: 0.9rem;">{{ report.topic }}</span>
          <span style="color: var(--v-grayColor-base);">•</span>
          <span>Report ID: #{{ report.id }}</span>
        </div>
      </div>
      <div
        v-if="reportType === 'SiloResearchTopic' && siloResearchType === 'custom-silo'"
        style="flex: 1;"
      >
        <div class="mb-1 d-flex align-center gap5">
          <span class="mr-1" style="font-weight: 700; font-size: 1rem;">Topic:</span>
          <b-dropdown aria-role="list" position="is-bottom-right">
            <template #trigger>
              <span
                style="color: #0075FF; font-weight: 700; font-size: 1rem;"
              >
                {{ report.topic || report.competitor }}
                <i class="material-icons">keyboard_arrow_down</i>
              </span>
            </template>

            <b-dropdown-item
              v-for="topic in topicsWithMetrics"
              :key="topic.id"
              :disbaled="topic.id === report.id"
              @click="$router.push(`/sites/${siteId}/campaign/${campaignId}/keyword-strategy/custom-silo/${report.siloResearchId}/metrics/${topic.id}`)"
            >
              <span :class="{'font-weight-bold': topic.id === report.id}">
                {{ topic.topic || topic.competitor }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <div style="color: var(--v-gray50Color-base); font-size: 0.8rem;">Report ID #{{ report.id }}</div>
      </div>

      <div class="d-flex align-center justify-end gap10">

        <div class="kr-advanced-metrics-toggle-row flex-shrink-0" v-if="!showGraphChart">
          <div class="kr-advanced-metrics-toggle-label">Advanced metrics</div>
          <v-switch
            inset
            hide-details
            dense
            color="#fff"
            :input-value="showAdvancedMetrics"
            @change="$emit('update:showAdvancedMetrics', $event)"
          />
        </div>

        <v-text-field
          class="table-search-field"
          outlined dense hide-details
          label="Search keywords"
          prepend-inner-icon="search"
          style="max-width: 300px; margin-top: -3px;"
          :value="search"
          @input="$emit('update:search', $event)"
        />
        
        <div class="flex-grow-1 d-flex align-center gap15">
          <template v-if="researchHistory.length > 0">
            <div class="card-small-title mr-5">Your results</div>
            <custom-auto-complete
              :items="researchHistory"
              :selected-value="report?.id"
              search-placeholder="Search reports"
              item-value="id"
              @updateSelectedValue="(id) => { changeResearchAction(id) }"
              @rename="renameResearchAction"
              @toggle-favorite="updateFavoriteStatus"
              @delete="deleteResearchAction"
            />
          </template>
          <div
            v-if="report?.aiTaskTopic"
            class="card-small-title" style="color: var(--v-blackColor-base);">AI Analysis Topic: <span class="normal-text">{{ report.aiTaskTopic }}</span></div>
        </div>
        <b-tooltip
          v-if="['KeywordResearch', 'SupportKeywordResearch', 'TopLevelSupportKeywordResearch'].indexOf(reportType) >= 0 "
          :label="rowsWithAdvancedMetrics.length < 10 ? `Must have at least 10 keywords` : ''"
          type="is-dark"
        >
          <b-dropdown
            class="cluster-with-ai-dropdown"
            append-to-body
            aria-role="menu"
            scrollable
            trap-focus
            :disabled="rowsWithAdvancedMetrics.length < 10 || isStartingAIAsyncTask || isAwaitingAIAsyncTaskStatus || currentAIAsyncTask?.is_pending_or_in_progress || (isWhiteGloveUser && !(isMissionControlSession || isContentOnlyView))"
            position="is-bottom-left"
          >
            <template #trigger>
              <v-btn
                outlined
                color="grayColor"
                class="button-with-icon text-none"
                :disabled="rowsWithAdvancedMetrics.length < 10 || isStartingAIAsyncTask || isAwaitingAIAsyncTaskStatus || currentAIAsyncTask?.is_pending_or_in_progress || (isWhiteGloveUser && !(isMissionControlSession || isContentOnlyView))"
                :style="{'margin-right': '10px'}"
              >
                <span style="font-weight: normal; color: var(--v-blackColor-base);">Cluster with AI</span>
              </v-btn>
            </template>

            <b-dropdown-item custom paddingless aria-role="listitem">
              <div class="blackColor--text">
                <div class="mb-3" style="font-size: 1rem; font-weight: 700;">Cluster keywords with AI</div>
                <div class="mb-3" style="line-height: 1.6;">
                  Type your topic or target keyword, and our AI + POP’s algorithm will group related keywords for you. We’ll suggest one Top Level keyword plus supporting ones for your {{ isWhiteGloveUser ? 'content plan' : 'silo' }} and you can edit, swap, or remove anything.
                </div>
                <div class="mb-3" style="line-height: 1.6;">
                  Example: if your page is about <b>“Office cleaning services”</b>, type that exact phrase here instead of something vague like <b>“cleaning.”</b>
                </div>
                <v-text-field outlined dense
                  :value="aiTopicFieldValue"
                  @input="$emit('update:aiTopicFieldValue', $event)"
                  placeholder="Enter topic or target keyword"
                  :error="aiTopicFieldValue.length > 80"
                  :error-messages="aiTopicFieldValue.length > 80?'Must be 1 to 80 characters':''"
                  color="grayColor"
                ></v-text-field>
              </div>
              <div class="d-flex justify-end gap10">
                <v-btn
                  color="profileAnchorColor"
                  class="whiteColor--text text-none"
                  :disabled="(aiTopicFieldValue.length < 1 || aiTopicFieldValue.length > 80) || (rowsWithAdvancedMetrics.length < 10 || isStartingAIAsyncTask || isAwaitingAIAsyncTaskStatus || currentAIAsyncTask?.is_pending_or_in_progress || (isWhiteGloveUser && !(isMissionControlSession || isContentOnlyView)))"
                  @click.stop="() => {
                    analyzeWithAI();
                  }"
                >
                  <span>Cluster with AI</span>
                </v-btn>

              </div>
            </b-dropdown-item>
          </b-dropdown>
        </b-tooltip>
        <div>
          <div class="keyword-filter-area" style="border-color: var(--v-grayColor-base);">
            <div class="filter-btn" :class="{'active': Boolean(Object.keys(allAppliedFilters).length)}" style="position:relative;" @click="$emit('update:showFilterPopup', true)">
              {{ Object.keys(allAppliedFilters).length }} filter{{Object.keys(allAppliedFilters).length === 1 ? '':'s'}} applied
              <span class="material-icons-outlined">keyboard_arrow_down</span>

              <div class="filter-popup"
                v-if="showFilterPopup" v-click-outside="() => $emit('update:showFilterPopup', false)"
                style="position: absolute; right: 0px; top: 50px; z-index: 12;">
                <div class="filter-item-label">Keyword Score</div>
                <div class="range-wrapper">
                  <v-range-slider hide-details
                    :min="filterKeywordScoreMinMax[0]"
                    :max="filterKeywordScoreMinMax[1]"
                    :value="filterKeywordScoreRange"
                    @input="$emit('update:filterKeywordScoreRange', $event)"
                    color="profileAnchorColor"
                    step="5"
                    stroke="10"
                    thumb-label="always"
                  ></v-range-slider>
                </div>
                <div class="filter-item-label">KGR Score</div>
                <div class="range-wrapper">
                  <v-range-slider hide-details
                    :min="filterKGRMinMax[0]"
                    :max="filterKGRMinMax[1]"
                    :value="filterKGRRange"
                    @input="$emit('update:filterKGRRange', $event)"
                    color="profileAnchorColor"
                    step="5"
                    stroke="10"
                    thumb-label="always"
                  ></v-range-slider>
                </div>
                <div class="filter-item-label">Difficulty Score</div>
                <div class="range-wrapper">
                  <v-range-slider hide-details
                    :min="filterDifficultyScoreMinMax[0]"
                    :max="filterDifficultyScoreMinMax[1]"
                    :value="filterDifficultyScoreRange"
                    @input="$emit('update:filterDifficultyScoreRange', $event)"
                    color="profileAnchorColor"
                    step="5"
                    stroke="10"
                    thumb-label="always"
                  ></v-range-slider>
                </div>
                <div class="d-flex align-center gap10">
                  <v-btn color="profileAnchorColor"
                    class="text-none whiteColor--text flex-grow-1"
                    @click="applyFitlersAction">
                    <span>Apply</span>
                  </v-btn>
                  <v-btn outlined color="blackColor"
                    class="text-none"
                    @click="() => {
                      updateFilterRangeReset()
                    }">
                    <span>Reset All</span>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!showGraphChart">
          <b-tooltip
            :label="isColumnVisible('_searchTrend') ? 'Hide search trend' : 'Show search trend'"
            type="is-dark"
          >
            <v-btn outlined
              :color="isColumnVisible('_searchTrend') ? 'profileAnchorColor' : 'grayColor'" class="button-with-icon text-none" style="min-width: initial; padding: 0 8px;"
              @click="() => {
                setColumnVisibility('_searchTrend', !isColumnVisible('_searchTrend'))
              }"
            >
              <i class="material-icons-outlined" :style="'font-size: 1.2rem;' + (isColumnVisible('_searchTrend') ? 'color: var(--v-profileAnchorColor-base);' : 'color: var(--v-gray2Color-base);')">timeline</i>
            </v-btn>
          </b-tooltip>
        </div>
        <div>
          <b-dropdown
            v-if="!showGraphChart"
            class="columns-visibility-dropdown"
            append-to-body
            aria-role="menu"
            scrollable
            max-height="200"
            trap-focus
            position="is-bottom-left"
          >
            <template #trigger>
              <div>
                <b-tooltip
                  label="Show/hide columns"
                  type="is-dark"
                >
                  <v-btn outlined
                    color="grayColor" class="button-with-icon text-none" style="min-width: initial; padding: 0 8px;"
                  >
                    <i class="material-icons-outlined" style="font-size: 1.2rem; color: var(--v-gray2Color-base);">view_week</i>
                  </v-btn>
                </b-tooltip>
              </div>
            </template>

            <b-dropdown-item custom paddingless aria-role="listitem">
              <div
                class="pb-4 mb-4"
                style="border-bottom: 2px solid #f5f5f5; font-weight: bold;"
              >{{hideableColumns.filter(column => isColumnVisible(column.key)).length}} out of {{hideableColumns.length}} selected</div>
              <div
                class="pb-4 mb-4"
                style="border-bottom: 2px solid #f5f5f5;"
              >
                <div
                  v-for="hideableColumn in hideableColumns" :key="hideableColumn.key"
                >
                  <v-checkbox
                    :disabled="hideableColumn.isAdvancedMetric && !showAdvancedMetrics"
                    color="#0075FF"
                    dense
                    hide-details
                    :label="hideableColumn.label"
                    :input-value="isColumnVisible(hideableColumn.key)"
                    @change="(value) => setColumnVisibility(hideableColumn.key, value)"
                  ></v-checkbox>
                </div>
              </div>

              <div class="d-flex align-center gap10">
                <v-btn text color="profileAnchorColor"
                  class="text-none"
                  @click="resetColumnVisibility"
                >
                  <v-icon
                    left
                    dark
                  >
                    mdi-refresh
                  </v-icon>
                  Reset
                </v-btn>
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </div>



        <div>
          <table-pdf-csv-download
            btnStyle="min-width: initial; padding: 0 8px;"
            btnLabel="Download"
            tooltipPosition="is-left"
            :iconOnly="true"
            :tableId="tableId"
            :title="title"
            :fileName="`${title ? title.replace(/\s/g, '') : 'KeywordResearch'}_${report?.id}`"
            :columns="visibleTableColumns"
            :rows="filteredRows"
          ></table-pdf-csv-download>
        </div>

        <template>
          <v-btn
            v-if="selectedCount && isWhiteGloveUser && isValidKeywordSelectionForContentOrder(selectedKeywords)"
            rounded
            color="profileAnchorColor"
            class="silo-content-order-toolbar-btn button-with-icon text-none whiteColor--text"
            tag="a"
            @click="$emit('update:showContentOrderFormForKeywords', [...selectedKeywords]); $emit('update:selectedKeywords', []);"
          >
            <i class="material-icons-outlined" style="margin-right: 10px;">shopping_basket</i>
            Order here ({{ selectedCount }})
          </v-btn>
        </template>
      </div>
    </div>

  </div>
</template>

<script>
import CustomAutoComplete from '@/common/CustomAutoComplete.vue';
import TablePdfCsvDownload from '@/components/Campaigns/TablePdfCsvDownload.vue';
import ResultTableModeSelector from '@/components/Campaigns/ResultTableModeSelector.vue';

export default {
  name: 'KeywordResearchResultControls',
  components: {
    CustomAutoComplete,
    TablePdfCsvDownload,
    ResultTableModeSelector,
  },
  props: {
    reportType: {
      default: null,
    },
    report: {
      default: null,
    },
    siloResearchType: {
      default: null,
    },
    parentReport: {
      default: null,
    },
    siteId: {
      type: Number,
      default: 0,
    },
    campaignId: {
      type: Number,
      default: 0,
    },
    topicsWithMetrics: {
      type: Array,
      default: () => [],
    },
    search: {
      type: String,
      default: '',
    },
    showAdvancedMetrics: {
      type: Boolean,
      default: true,
    },
    researchHistory: {
      type: Array,
      default: () => [],
    },
    changeResearchAction: {
      type: Function,
      required: true,
    },
    renameResearchAction: {
      type: Function,
      required: true,
    },
    updateFavoriteStatus: {
      type: Function,
      required: true,
    },
    deleteResearchAction: {
      type: Function,
      required: true,
    },
    rowsWithAdvancedMetrics: {
      type: Array,
      default: () => [],
    },
    isStartingAIAsyncTask: {
      type: Boolean,
      default: false,
    },
    isAwaitingAIAsyncTaskStatus: {
      type: Boolean,
      default: false,
    },
    currentAIAsyncTask: {
      default: null,
    },
    isWhiteGloveUser: {
      type: Boolean,
      default: false,
    },
    isMissionControlSession: {
      type: Boolean,
      default: false,
    },
    isContentOnlyView: {
      type: Boolean,
      default: false,
    },
    aiTopicFieldValue: {
      type: String,
      default: '',
    },
    analyzeWithAI: {
      type: Function,
      required: true,
    },
    showFilterPopup: {
      type: Boolean,
      default: false,
    },
    allAppliedFilters: {
      type: Object,
      default: () => ({}),
    },
    filterKeywordScoreMinMax: {
      type: Array,
      default: () => [0, 100],
    },
    filterKeywordScoreRange: {
      type: Array,
      default: () => [0, 100],
    },
    filterKGRMinMax: {
      type: Array,
      default: () => [0, 100],
    },
    filterKGRRange: {
      type: Array,
      default: () => [0, 100],
    },
    filterDifficultyScoreMinMax: {
      type: Array,
      default: () => [0, 100],
    },
    filterDifficultyScoreRange: {
      type: Array,
      default: () => [0, 100],
    },
    applyFitlersAction: {
      type: Function,
      required: true,
    },
    updateFilterRangeReset: {
      type: Function,
      required: true,
    },
    isColumnVisible: {
      type: Function,
      required: true,
    },
    setColumnVisibility: {
      type: Function,
      required: true,
    },
    resetColumnVisibility: {
      type: Function,
      required: true,
    },
    hideableColumns: {
      type: Array,
      default: () => [],
    },
    hiddenColumnsKeys: {
      type: Array,
      default: () => [],
    },
    showGraphChart: {
      type: Boolean,
      default: false,
    },
    tableId: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    visibleTableColumns: {
      type: Array,
      default: () => [],
    },
    filteredRows: {
      type: Array,
      default: () => [],
    },
    selectAllChecked: {
      type: Boolean,
      default: false,
    },
    selectAllIndeterminate: {
      type: Boolean,
      default: false,
    },
    selectedCount: {
      type: Number,
      default: 0,
    },
    selectAllDisabled: {
      type: Boolean,
      default: false,
    },
    hasLabeledKeywords: {
      type: Boolean,
      default: false,
    },
    showHiddenRowsModal: {
      type: Boolean,
      default: false,
    },
    hiddenRows: {
      type: Array,
      default: () => [],
    },
    selectedKeywords: {
      type: Array,
      default: () => [],
    },
    isValidKeywordSelectionForContentOrder: {
      type: Function,
    },
    showContentOrderFormForKeywords: {
      type: Array,
      default: () => [],
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./keyword-research.scss";

.select-all-group {
  gap: 6px;
}

.wg-result-selected-count {
  margin-left: 8px;
  color: #ff5700;
  font-size: 0.875rem;
  font-weight: 500;
}

::v-deep {
  .wg-result-select-all-checkbox {
    label {
      color: var(--v-mainColor-base);
      font-size: 0.9rem;
    }

    .v-input--selection-controls__input {
      border: 1px solid #999999 !important;
      border-radius: 4px;
      height: 20px !important;
      width: 20px !important;
    }

    &.v-input--is-label-active {
      .v-icon {
        font-size: 16px !important;
      }
    }
  }
}

::v-deep .table-search-field {
  .v-icon {
    color: var(--v-gray36Color-base);
  }
  .v-label {
    color: var(--v-gray36Color-base);
    font-size: 0.85rem;
  }
  fieldset {
    border-color: var(--v-gray12Color-base);
  }
}

.kr-advanced-metrics-toggle-row {
  display: flex;
  align-items: center;

  .kr-advanced-metrics-toggle-label {
    font-weight: 700;
    color: var(--v-blackColor-base, #1a1a1a);
    white-space: nowrap;
  }

  .v-input--switch {
    transform: scale(0.6);
  }

  .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
  }

  ::v-deep {
    .v-input--selection-controls__ripple {
      color: #fff !important;
      caret-color: #fff !important;
    }

    .v-input--switch__thumb {
      width: 13px !important;
      height: 13px !important;
      top: calc(50% - 8px) !important;
    }

    .v-input--switch__track {
      opacity: 0.8 !important;
    }

    .v-input--is-label-active {
      .v-input--switch__track {
        opacity: 1 !important;
        color: #11a33d !important;
        caret-color: #11a33d !important;
      }
    }
  }
}
</style>

<style lang="scss">
.columns-visibility-dropdown {
  .dropdown-menu {
    padding-top: 17px;
  }
  .dropdown-content {
    border-radius: 5px;
    padding: 15px;
    width: 300px;
    border: 1px solid var(--v-grayColor-base);
    background: var(--v-whiteColor-base);
    box-shadow: 0px 2.563px 20.825px 0px rgba(0, 0, 0, 0.12);
  }
}

.cluster-with-ai-dropdown {
  .dropdown-menu {
    padding-top: 17px;
  }
  .dropdown-content {
    border-radius: 5px;
    padding: 20px;
    width: 400px;
    border: 1px solid var(--v-grayColor-base);
    background: var(--v-whiteColor-base);
    box-shadow: 0px 2.563px 20.825px 0px rgba(0, 0, 0, 0.12);
  }
}
</style>
